import React, { useState, useEffect } from "react";
import { Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import {
  FiCalendar,
  FiClock,
  FiShare2,
  FiCopy,
  FiCheck,
  FiArrowLeft,
  FiVideo,
  FiImage,
  FiUser,
} from "react-icons/fi";
import { FaWhatsapp, FaFacebook, FaTwitter } from "react-icons/fa";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://japl-backend-v2jm.onrender.com/api"
    : "http://localhost:5000/api";

const BLOG_CACHE_KEY = "blogListCacheV2";

// Helper to construct a public, crawler-accessible share URL
export const getPublicShareUrl = (blogId) => {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("192.168."))
  ) {
    return `https://japl.co.in/blogs/${blogId}`;
  }
  return `${window.location.origin}/blogs/${blogId}`;
};

// Helper to extract a clean, professional summary excerpt for WhatsApp & social share
export const getCleanExcerpt = (blog, maxLen = 220) => {
  if (blog?.summary && blog.summary.trim()) {
    const s = blog.summary.trim();
    return s.length > maxLen ? s.slice(0, maxLen - 3) + "..." : s;
  }
  if (blog?.content) {
    const plain = blog.content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (plain.length > maxLen) {
      return plain.slice(0, maxLen - 3) + "...";
    }
    return plain;
  }
  return "";
};

// Professional WhatsApp message builder & launcher
export const shareBlogOnWhatsApp = (blog, blogId) => {
  const title = blog?.title || "Janatha Automobiles Blog";
  const excerpt = getCleanExcerpt(blog, 220);
  const publicUrl = getPublicShareUrl(blogId || blog?._id);

  let text = `🚛 *JANATHA AUTOMOBILES | LATEST UPDATE*\n`;
  text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  text += `📌 *${title}*\n\n`;
  if (excerpt) {
    text += `📝 *Summary:*\n${excerpt}\n\n`;
  }
  text += `🔗 *Read Full Story & View Photos/Video:*\n${publicUrl}`;

  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
};

// Helper to calculate estimated read time
const getReadingTime = (text = "") => {
  const wordsPerMinute = 200;
  const wordCount = text.replace(/<[^>]*>/g, "").split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
};

// Media Player / Image component for blog cover
const BlogMediaBanner = ({ coverUrl, mediaType = "image", title = "", isFull = false }) => {
  if (!coverUrl) return null;

  const isVideo =
    mediaType === "video" ||
    /\.(mp4|webm|mov|mkv)$/i.test(coverUrl) ||
    coverUrl.includes("youtube.com") ||
    coverUrl.includes("youtu.be");

  if (isVideo) {
    if (coverUrl.includes("youtube.com") || coverUrl.includes("youtu.be")) {
      let videoId = "";
      if (coverUrl.includes("youtu.be/")) {
        videoId = coverUrl.split("youtu.be/")[1]?.split("?")[0];
      } else {
        try {
          videoId = new URLSearchParams(new URL(coverUrl).search).get("v");
        } catch {
          videoId = "";
        }
      }
      return (
        <div className={`aspect-video w-full rounded-2xl overflow-hidden shadow-lg ${isFull ? "max-h-[500px]" : "h-48"}`}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&playsinline=1`}
            title={title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    return (
      <div className={`w-full bg-black rounded-2xl overflow-hidden shadow-lg ${isFull ? "max-h-[500px]" : "h-48"}`}>
        <video
          src={coverUrl}
          autoPlay
          muted
          loop
          playsInline
          controls={isFull}
          className="w-full h-full object-contain mx-auto"
        />
      </div>
    );
  }

  return (
    <div className={`w-full overflow-hidden rounded-2xl ${isFull ? "max-h-[500px]" : "h-52"} bg-gray-100`}>
      <img
        src={coverUrl}
        alt={title}
        className="w-full h-full object-cover object-center group-hover:scale-105 transition duration-300"
      />
    </div>
  );
};

// -------------------------------------------------------------
// INDIVIDUAL BLOG DETAIL PAGE (/blogs/:id)
// -------------------------------------------------------------
function BlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    async function fetchBlogDetail() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/blogs/${id}`);
        if (!res.ok) throw new Error("Blog not found");
        const data = await res.json();
        setBlog(data);

        // Fetch related blogs for footer cards
        const listRes = await fetch(`${API_BASE_URL}/blogs`);
        if (listRes.ok) {
          const list = await listRes.json();
          setRelatedBlogs(list.filter((b) => b._id !== id).slice(0, 3));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchBlogDetail();
  }, [id]);

  // Dynamically update document title and Open Graph meta tags in DOM
  useEffect(() => {
    if (blog?.title) {
      document.title = `${blog.title} | Janatha Automobiles`;

      const updateTag = (selector, attribute, value) => {
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement("meta");
          if (selector.includes("[property=")) {
            const prop = selector.match(/\[property="([^"]+)"\]/)?.[1];
            if (prop) el.setAttribute("property", prop);
          } else if (selector.includes("[name=")) {
            const name = selector.match(/\[name="([^"]+)"\]/)?.[1];
            if (name) el.setAttribute("name", name);
          }
          document.head.appendChild(el);
        }
        el.setAttribute(attribute, value);
      };

      const desc = blog.summary || blog.metaDescription || blog.title;
      const img = blog.coverImage || "https://japl.co.in/japl-logo.png";
      const currentUrl = window.location.href;

      updateTag('meta[name="description"]', "content", desc);
      updateTag('meta[property="og:title"]', "content", blog.title);
      updateTag('meta[property="og:description"]', "content", desc);
      updateTag('meta[property="og:image"]', "content", img);
      updateTag('meta[property="og:image:secure_url"]', "content", img);
      updateTag('meta[property="og:url"]', "content", currentUrl);
      updateTag('meta[property="og:type"]', "content", "article");
      updateTag('meta[name="twitter:card"]', "content", "summary_large_image");
      updateTag('meta[name="twitter:title"]', "content", blog.title);
      updateTag('meta[name="twitter:description"]', "content", desc);
      updateTag('meta[name="twitter:image"]', "content", img);
    }

    return () => {
      document.title = "Janatha Automobiles";
    };
  }, [blog]);

  // Always use the canonical public URL for sharing — never localhost
  const shareUrl = getPublicShareUrl(id);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleWhatsAppShare = () => {
    shareBlogOnWhatsApp(blog, id);
  };

  const handleNativeShare = async () => {
    const excerpt = getCleanExcerpt(blog, 150);
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog?.title,
          text: excerpt || blog?.title,
          url: shareUrl,
        });
      } catch (err) {
        console.log("Share cancelled", err);
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600 font-medium">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-50 py-20 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-3xl p-8 shadow-xl text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Blog Post Not Found</h2>
          <p className="text-gray-500 mb-6">The article you are looking for might have been moved or deleted.</p>
          <button
            onClick={() => navigate("/blogs")}
            className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
          >
            ← Back to All Blogs
          </button>
        </div>
      </div>
    );
  }

  return (
    <article className="bg-gray-50 min-h-screen pt-28 md:pt-32 pb-16">
      <div className="w-full mx-auto px-4 sm:px-6">
        {/* Navigation & Back Button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/blogs")}
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-blue-700 transition bg-white px-4 py-2 rounded-xl shadow-sm border"
          >
            <FiArrowLeft /> Back to Blogs
          </button>

          {/* Direct Link Share Pill */}
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 text-xs font-semibold bg-blue-50 border border-blue-200 text-blue-700 px-3.5 py-1.5 rounded-full hover:bg-blue-100 transition"
          >
            {copied ? (
              <>
                <FiCheck className="text-green-600 text-sm" /> Direct Link Copied!
              </>
            ) : (
              <>
                <FiCopy /> Copy Shareable Link
              </>
            )}
          </button>
        </div>

        {/* Main Article Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-10 mb-12">
          {/* Header Info */}
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-500 mb-4">
            <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
              <FiUser /> {blog.author || "JAPL Team"}
            </span>
            <span className="flex items-center gap-1">
              <FiCalendar /> {new Date(blog.publishedDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <FiClock /> {getReadingTime(blog.content)}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
            {blog.title}
          </h1>

          {/* Summary Callout if available */}
          {blog.summary && (
            <p className="text-lg md:text-xl text-gray-600 font-medium italic border-l-4 border-blue-600 pl-4 py-1 mb-8">
              {blog.summary}
            </p>
          )}

          {/* Media Banner (Image or Video) */}
          <div className="mb-8">
            <BlogMediaBanner
              coverUrl={blog.coverImage}
              mediaType={blog.mediaType}
              title={blog.title}
              isFull={true}
            />
          </div>

          {/* Sticky / Social Share Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-200 mb-8">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
              <FiShare2 className="text-blue-600" /> Share this Blog Post:
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 bg-white border border-gray-300 hover:border-blue-500 text-gray-700 px-3.5 py-1.5 rounded-xl text-xs font-semibold shadow-sm transition"
              >
                {copied ? <FiCheck className="text-green-600" /> : <FiCopy />}
                {copied ? "Copied!" : "Copy Link"}
              </button>

              <button
                onClick={handleWhatsAppShare}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold shadow-sm transition"
              >
                <FaWhatsapp className="text-sm" /> WhatsApp
              </button>

              <button
                onClick={handleNativeShare}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold shadow-sm transition sm:hidden"
              >
                <FiShare2 /> Share
              </button>
            </div>
          </div>

          {/* Blog Content */}
          <div
            className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-sans"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Bottom Share Bar */}
          <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-gray-800">Enjoyed this article?</p>
              <p className="text-xs text-gray-500">Share it with your clients or colleagues directly.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md hover:bg-blue-700 transition"
              >
                {copied ? <FiCheck /> : <FiShare2 />}
                {copied ? "Link Copied!" : "Share Post Link"}
              </button>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">More Articles You Might Like</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              {relatedBlogs.map((item) => (
                <Link
                  key={item._id}
                  to={`/blogs/${item._id}`}
                  className="bg-white rounded-2xl shadow hover:shadow-xl border transition flex flex-col overflow-hidden group"
                >
                  <div className="h-40 bg-gray-100 overflow-hidden">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h4 className="font-bold text-gray-900 group-hover:text-blue-600 line-clamp-2 mb-2 text-sm">
                      {item.title}
                    </h4>
                    <span className="text-xs text-blue-600 font-semibold mt-auto">Read Article →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

// -------------------------------------------------------------
// PUBLIC BLOGS LIST PAGE (/blogs)
// -------------------------------------------------------------
function BlogListPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const cached = localStorage.getItem(BLOG_CACHE_KEY);
    if (cached) {
      try {
        const { data } = JSON.parse(cached);
        setBlogs(data);
        setCacheChecked(true);
      } catch {}
    }

    async function fetchBlogs() {
      try {
        const res = await fetch(`${API_BASE_URL}/blogs`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setBlogs(data);
        localStorage.setItem(
          BLOG_CACHE_KEY,
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setCacheChecked(true);
      }
    }
    fetchBlogs();
  }, []);

  const shareIndividualOnWhatsApp = (e, blog) => {
    e.stopPropagation(); // prevent card click
    shareBlogOnWhatsApp(blog, blog._id);
    setCopiedId(blog._id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-28 md:pt-32 pb-16">
      <div className="w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h1 className="uppercase text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
            Blogs & Insights
          </h1>
          <p className="text-gray-600 text-base">
            Stay updated with our latest industry news, product releases, and expert vehicle tips.
          </p>
          <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full mt-4" />
        </div>

        {/* Loading Spinner */}
        {loading && blogs.length === 0 && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Blog Cards Grid */}
        {!loading && blogs.length === 0 && cacheChecked ? (
          <div className="bg-white rounded-3xl p-12 text-center text-gray-500 shadow border max-w-md mx-auto">
            No blog posts published yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog) => (
              <div
                key={blog._id}
                onClick={() => navigate(`/blogs/${blog._id}`)}
                className="bg-white rounded-3xl shadow-md hover:shadow-2xl border border-gray-100 transition duration-300 group flex flex-col overflow-hidden cursor-pointer relative"
              >
                {/* Media Type Badge */}
                <div className="absolute top-3 left-3 z-10 bg-black/60 backdrop-blur-md text-white text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  {blog.mediaType === "video" ? (
                    <>
                      <FiVideo className="text-red-400" /> Video
                    </>
                  ) : (
                    <>
                      <FiImage className="text-blue-400" /> Article
                    </>
                  )}
                </div>

                {/* Individual WhatsApp Share Quick Button */}
                <button
                  type="button"
                  onClick={(e) => shareIndividualOnWhatsApp(e, blog)}
                  className="absolute top-3 right-3 z-10 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold p-2 rounded-full shadow-md transition transform active:scale-95"
                  title="Share on WhatsApp"
                >
                  {copiedId === blog._id ? (
                    <FiCheck className="text-white text-sm" />
                  ) : (
                    <FaWhatsapp className="text-sm" />
                  )}
                </button>

                {/* Banner Media */}
                <BlogMediaBanner
                  coverUrl={blog.coverImage}
                  mediaType={blog.mediaType}
                  title={blog.title}
                  isFull={false}
                />

                {/* Details */}
                <div className="flex-1 p-6 flex flex-col">
                  <div className="flex items-center text-gray-500 text-xs gap-3 mb-2 font-medium">
                    <span className="flex items-center gap-1">
                      <FiCalendar /> {new Date(blog.publishedDate).toLocaleDateString()}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <FiClock /> {getReadingTime(blog.content)}
                    </span>
                  </div>

                  <h3 className="font-bold text-xl text-gray-900 group-hover:text-blue-600 transition line-clamp-2 mb-2">
                    {blog.title}
                  </h3>

                  {blog.summary && (
                    <p className="text-gray-600 text-xs line-clamp-2 mb-4">
                      {blog.summary}
                    </p>
                  )}

                  <div className="mt-auto pt-4 border-t flex items-center justify-between text-xs font-semibold text-blue-600 group-hover:text-blue-700">
                    <span>Read Full Story &rarr;</span>
                    {copiedId === blog._id && (
                      <span className="text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded flex items-center gap-1">
                        <FaWhatsapp /> Shared!
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Main Blog Component handling nested routing
export default function BlogPage() {
  return (
    <Routes>
      <Route path="/" element={<BlogListPage />} />
      <Route path="/:id" element={<BlogDetailPage />} />
    </Routes>
  );
}
